<template>
  <div class="container p-5">
    <h1 class="title">Connect</h1>
    <div class="columns">
      <!-- 1. Destinations Section -->
      <div class="column is-4">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Destinations</p>
          </header>
          <div class="card-content">
            <table class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dest in destinations" :key="dest.number">
                  <td>{{ dest.name }}</td>
                  <td>
                    <button 
                      class="button is-small"
                      :class="activeCall === dest.number ? 'is-danger' : 'is-link'"
                      :disabled="activeCall && activeCall !== dest.number"
                      @click="toggleCall(dest.number)"
                    >
                      {{ activeCall === dest.number ? 'Disconnect' : 'Connect' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 2. Status Section -->
      <div class="column is-8">
        <div class="columns is-multiline">
          <!-- Connection Status -->
          <div class="column is-6">
            <div class="box is-light">
              <h2 class="subtitle">Status</h2>
              <div class="tags">
                    <span :class="['tag', sipStatus.connection === true ? 'is-success' : 'is-danger']">
                      {{ sipStatus.connection ? 'SIP Connected' : 'Not Connected' }}
                    </span>

                    <span :class="['tag', sipStatus.registration === true ? 'is-success' : 'is-danger']">
                      {{ sipStatus.registration ? 'SIP Registered' : 'Not Registered' }}
                    </span>
                  <span :class="['tag', activeCall ? 'is-success' : 'is-text']">
                    <span>{{ getConnectionStatus(sipStatus.callstatus, activeCall) }}</span>
                  </span>
              </div>

              <h2 class="subtitle">Audio</h2>
              <div class="field is-grouped is-grouped-multiline">
                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag">Codec</span>
                    <span class="tag is-primary">{{ [callStats.encode.codec, callStats.decode.codec].filter(c => c).join(' / ') || 'No Codec' }}</span>
                  </div>

                  <div class="tags has-addons">
                    <span class="tag">Bitrate</span>
                    <span class="tag is-primary">{{ [callStats.encode.bitrate, callStats.decode.bitrate].filter(c => c).join(' / ') || 'N/A' }} kbps</span>
                  </div>
                </div>
                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag">Sample Rate</span>
                    <span class="tag is-primary">{{ [callStats.encode.sampleRate, callStats.decode.sampleRate].filter(c => c).join(' / ') || 'N/A' }} Hz</span>
                  </div>
                </div>                
              </div>
            </div>
          </div>
          
          <div class="column is-6">
            <div class="box">
              <h2 class="subtitle">Audio Connections</h2>
              <table class="table is-fullwidth is-hoverable">
                <thead>
                  <tr>
                    <th>Destination</th>
                    <th>Number</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="connection in bridgeConnections" :key="connection?.id">
                    <td>{{ connection.caller_name || 'Unknown' }}</td>
                    <td>{{ connection.caller_number }}</td>
                    <td>
                      <div class="buttons are-small">
                        <button 
                          @click="hangupCall(connection.id)" 
                          class="button is-primary"
                        >
                          Disconnect
                        </button>                   
                      </div>
                    </td>
                  </tr>
                  <tr v-if="bridgeConnections.length === 0">
                    <td colspan="4" class="has-text-centered has-text-grey">
                      No connections.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>            
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-4">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Audio Devices</p>
          </header>
          <div class="card-content">        
            <AudioDeviceSelector
              :disabled="!!activeCall"
              @update:inputDevice="handleInputUpdate"
              @update:outputDevice="handleOutputUpdate"
            />
          <div class="field">
            <div class="buttons has-addons are-small is-centered">
              <!-- Echo Cancellation -->
              <button 
                class="button" 
                :class="{ 'is-success is-selected': audioConstraints.echoCancellation }" 
                @click="audioConstraints.echoCancellation = !audioConstraints.echoCancellation"
              >
                Echo Cancellation
              </button>

              <!-- Noise Suppression -->
              <button 
                class="button" 
                :class="{ 'is-success is-selected': audioConstraints.noiseSuppression }" 
                @click="audioConstraints.noiseSuppression = !audioConstraints.noiseSuppression"
              >
                Noise Suppression
              </button>

              <!-- AGC -->
              <button 
                class="button" 
                :class="{ 'is-success is-selected': audioConstraints.autoGainControl }" 
                @click="audioConstraints.autoGainControl = !audioConstraints.autoGainControl"
              >
                AGC
              </button>
            </div>
          </div>            
          </div>
        </div>
      </div>
      <div class="column is-8">
        <div class="box">
          <p class="subtitle is-4">Levels</p>
          <div class="columns is-multiline">
            <div class="column is-6">
                  <AudioMeter :stream="remoteStream" label="Remote"/>
            </div>
            <div class="column is-6">
              <AudioFader id="fader-remote" class="mt-4" label="Remote" @update="handleRemoteVol"/>
            </div>
            <div class="column is-6">
              <AudioMeter :stream="localStream" label="Local"/>
            </div>
            <div class="column is-6">
              <AudioFader id="fader-local" class="mt-4" label="Local" @update="handleLocalVol" />
            </div>            
          </div>
        </div>
      </div>
      
    </div>

    <!-- 3. Side-by-Side Line Graphs using vue-chartjs -->
    <div class="columns is-multiline">
      <div class="column is-4" v-for="type in ['loss', 'jitter', 'rtt']" :key="type">
        <div class="box">
          <p class="subtitle is-6">
            <span class="is-capitalized">{{ type === 'rtt' ? "Round-Trip Time": type }}</span><span>{{ type === 'loss' ? ' (%)' : ' (ms)' }}</span>
          </p>
          <div style="height: 150px; position: relative;">
            <Line :data="getChartData(type)" :options="chartOptions" />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onUnmounted, onMounted, watch } from 'vue';
import AudioMeter from '@/components/AudioMeter.vue';
import AudioFader from '@/components/AudioFader.vue';
import { connectLogic, webRTCService, makeCall, hangup, perfStats, audioConst } from '@/services/connect'
import AudioDeviceSelector from '@/components/AudioDeviceSelector.vue';
import api from '@/services/api';
import { getWebSocketState } from '@/services/websocket';
import { hangupCall } from '@/services/callmanager';

const { sipStatus, CALL_IDLE, CALL_RINGING, CALL_ESTABLISHED, CALL_FAILED, callStats, activeCall, getDuration, inputDevice, outputDevice, updateGain } = webRTCService()
const { localStream, remoteStream, userAgent } = connectLogic()

const { performanceHistory, dataPoints } = perfStats()

const { app_socket, bridgeConnections } = getWebSocketState();

import { Line } from 'vue-chartjs';
import { 
  Chart as ChartJS, Title, Tooltip, Legend, LineElement, 
  PointElement, CategoryScale, LinearScale 
} from 'chart.js';

const handleInputUpdate = (id) => {
  inputDevice.value = id;
};

const handleOutputUpdate = (id) => {
  outputDevice.value = id;
};

const { audioConstraints, updateAudioConstraints } = audioConst();

watch(audioConstraints, (newVal) => {
  updateAudioConstraints();
}, { deep: true });

// Register Chart.js modules
ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);

const callStatusText = [
  'Idle',
  'Ringing',
  'Established',
  'Failed'
];

const getConnectionStatus = (callstatus, activeCall) => {
  switch(callstatus) {
    case 0:
      return "No Audio Connection";
    case 1:
      return "Connecting";      
    case 3:
      return "Connection Failed";
    case 2:
      return `Connected (${callDuration.value || '00:00:00'})`;
    default:
      return "Unknown";
  }
};

const inviter = ref(null)
let timer = null
const callDuration = ref(null)

// 1. Initialize the reactive variable
const destinations = ref([]);

// 2. Create the fetch function
const fetchDestinations = async () => {
  try {
    const res = await api.get('/api/admin/destinations');
    destinations.value = res.data;
  } catch (err) {
    console.error("Failed to load destinations:", err);
  }
};

// Helper to format data for Chart.js
const getChartData = (metric) => {
  return {
    labels: Array(dataPoints).fill(''), // Empty labels for time-series look
    datasets: [
      {
        label: 'Inbound (Decode)',
        backgroundColor: '#3273dc',
        borderColor: '#3273dc',
        data: performanceHistory.value.map(h => h.decode[metric]),
        tension: 0.3, // Smooth lines
        pointRadius: 0
      },
      {
        label: 'Outbound (Encode)',
        backgroundColor: '#48c774',
        borderColor: '#48c774',
        data: performanceHistory.value.map(h => h.encode[metric]),
        tension: 0.3,
        pointRadius: 0
      }
    ]
  };
};

// Global Chart Options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false, // Set to false for high-frequency "live" updates
  plugins: {
    legend: { display: false } // We can use custom Bulma legends instead
  },
  scales: {
    x: { display: false },
    y: {
      beginAtZero: true,
      grid: { color: '#f5f5f5' },
      ticks: { font: { size: 10 } }
    }
  }
};

// Methods
const toggleCall = (number) => {
  if (activeCall.value === number) {
    stopCall();
  } else {
    startCall(number);
  }
};

const startCall = (number) => {
  const inviterSession = makeCall(userAgent.value, number);
  inviterSession.then(session => {
    inviter.value = session;
  }).catch(error => {
    console.error("Error establishing call:", error);
  });
};

const stopCall = () => {
  activeCall.value = null;
  hangup();
};

const handleRemoteVol = (data) => {
  updateGain("remote", data.level, data.muted)
}

const handleLocalVol = (data) => {
  updateGain("local", data.level, data.muted)
}

onMounted(() => {
  fetchDestinations();
  timer = setInterval(() => {
      callDuration.value = getDuration()
  }, 1000)
});

onUnmounted(() => {
  clearInterval(timer);
});


</script>

<style scoped>
/* .tag { width: 100%; justify-content: center; } */
.graph-container {
  background: #fff;
  border-left: 2px solid #dbdbdb;
  border-bottom: 2px solid #dbdbdb;
}
</style>
